import { useEffect, useMemo, useState } from "react";
import type { PlatformClient } from "../../platform/supabase";

type Overview={
  learner:{
    learnerId:string;
    displayName:string;
    status:string;
    gradeLevel?:string|null;
    classLabel?:string|null;
    schoolName?:string|null;
  };
  journey?:{
    journeyId:string;
    status:string;
    startedAt:string;
    closedAt?:string|null;
  }|null;
  counts:{
    experiences:number;
    evaluations:number;
    achievements:number;
    credentials:number;
  };
};

type Goal={
  goalId:string;
  goalType:string;
  status:string;
  title:string;
  description?:string|null;
  targetOn?:string|null;
  effectiveAt:string;
  isLatest:boolean;
};

type NextAction={
  entryId:string;
  title:string;
  body?:string|null;
  occurredAt:string;
  actionOn?:string|null;
  actionStatus?:string|null;
  isLatest:boolean;
};

type TimelineItem={
  occurredAt:string;
  eventType:string;
  resourceId:string;
  title:string;
  description?:string|null;
  visibility:string;
  metadata:Record<string,unknown>;
};

type TimelineData={
  goals:Goal[];
  nextActions:NextAction[];
  timeline:TimelineItem[];
};

function formatDate(value?:string|null){
  if(!value)return "-";
  return new Date(value).toLocaleDateString("ko-KR",{
    year:"numeric",month:"2-digit",day:"2-digit"
  });
}

function goalTypeLabel(value:string){
  const map:Record<string,string>={
    EXPLORATION:"탐색",
    LEARNING:"학습",
    EXPERIENCE:"경험",
    EDUCATION:"진학",
    CAREER:"진로",
    OTHER:"기타"
  };
  return map[value] || value;
}

function goalStatusLabel(value:string){
  const map:Record<string,string>={
    DRAFT:"초안",
    ACTIVE:"진행 중",
    ACHIEVED:"달성",
    PAUSED:"잠시 멈춤",
    RETIRED:"종료"
  };
  return map[value] || value;
}

function eventLabel(value:string){
  const map:Record<string,string>={
    ENROLLMENT:"학년도",
    GOAL:"목표",
    TRANSITION_POINT:"전환점",
    NEXT_ACTION:"다음 행동",
    MILESTONE:"마일스톤",
    EXPERIENCE:"경험",
    ASSESSMENT:"검사",
    EVIDENCE:"증빙",
    EVALUATION:"평가",
    COMPETENCY:"역량",
    ACHIEVEMENT:"성취",
    CREDENTIAL:"Life Stamp"
  };
  return map[value] || value;
}

export function WalletJourneyTimelinePage({
  client,
  onNavigate
}:{
  client:PlatformClient;
  onNavigate:(path:string)=>void;
}){
  const [overview,setOverview]=useState<Overview|null>(null);
  const [journey,setJourney]=useState<TimelineData|null>(null);
  const [filter,setFilter]=useState("ALL");
  const [error,setError]=useState("");

  useEffect(()=>{
    let active=true;
    Promise.all([
      client.rpc("wallet_overview"),
      client.rpc("wallet_journey_timeline")
    ]).then(([overviewResult,timelineResult])=>{
      if(!active)return;
      if(overviewResult.error){setError(overviewResult.error.message);return;}
      if(timelineResult.error){setError(timelineResult.error.message);return;}
      setOverview(overviewResult.data as Overview);
      setJourney(timelineResult.data as TimelineData);
    });
    return()=>{active=false;};
  },[client]);

  const currentGoals=useMemo(
    ()=>journey?.goals.filter((item)=>item.isLatest && ["ACTIVE","PAUSED"].includes(item.status)) || [],
    [journey]
  );

  const latestActions=useMemo(
    ()=>journey?.nextActions
      .filter((item)=>item.isLatest && (item.actionStatus || "ACTIVE")==="ACTIVE")
      .slice(0,4) || [],
    [journey]
  );

  const filteredTimeline=useMemo(()=>{
    const rows=journey?.timeline || [];
    if(filter==="ALL")return rows;
    if(filter==="GROWTH"){
      return rows.filter((item)=>["EVALUATION","COMPETENCY","ACHIEVEMENT","CREDENTIAL"].includes(item.eventType));
    }
    if(filter==="EXPERIENCE"){
      return rows.filter((item)=>["EXPERIENCE","ASSESSMENT","EVIDENCE"].includes(item.eventType));
    }
    if(filter==="DIRECTION"){
      return rows.filter((item)=>["GOAL","TRANSITION_POINT","NEXT_ACTION","MILESTONE","ENROLLMENT"].includes(item.eventType));
    }
    return rows;
  },[journey,filter]);

  if(error)return <p className="error">{error}</p>;
  if(!overview || !journey)return <p className="muted">Life Passport를 불러오는 중…</p>;

  const primaryGoal=currentGoals[0] || null;
  const primaryAction=latestActions[0] || null;
  const totalRecords=
    overview.counts.experiences+
    overview.counts.evaluations+
    overview.counts.achievements+
    overview.counts.credentials;

  return (
    <section className="wallet-home-v2">
      <section className="passport-cover">
        <div className="passport-cover-copy">
          <div className="passport-brand-line">
            <span className="passport-monogram">RS</span>
            <span>LIFE PASSPORT</span>
          </div>
          <p className="eyebrow">MY CAREER JOURNEY</p>
          <h2>{overview.learner.displayName}</h2>
          <p className="passport-school">
            {overview.learner.schoolName || "학교 미확인"}
            {overview.learner.gradeLevel ? " · "+overview.learner.gradeLevel+"학년" : ""}
            {overview.learner.classLabel ? " · "+overview.learner.classLabel+"반" : ""}
          </p>
          <div className="passport-status-row">
            <span className="passport-live-dot" />
            <strong>{overview.journey?.status==="ACTIVE" ? "진로성장 여정 진행 중" : "나의 진로성장 기록"}</strong>
            {overview.journey?.startedAt && <small>{formatDate(overview.journey.startedAt)} 시작</small>}
          </div>
        </div>

        <div className="passport-cover-stat">
          <span>MY RECORD</span>
          <strong>{totalRecords}</strong>
          <small>확인된 성장 기록</small>
        </div>
      </section>

      <nav className="wallet-quick-grid" aria-label="Life Passport 빠른 메뉴">
        <QuickLink href="/credentials" icon="✦" title="Life Stamp" description="내가 획득한 디지털배지" />
        <QuickLink href="/experiences" icon="◇" title="경험" description="활동과 참여 기록" />
        <QuickLink href="/assessments" icon="◎" title="검사" description="배정 검사와 결과" />
        <QuickLink href="/portfolio" icon="✎" title="만들기" description="선택한 기록으로 문서 만들기" />
        <QuickLink href="/share" icon="↗" title="공유" description="선택한 기록만 안전하게" />
      </nav>

      <section className="wallet-home-section">
        <div className="wallet-home-heading">
          <div>
            <p className="eyebrow">NOW</p>
            <h2>지금의 방향</h2>
          </div>
          <span className="wallet-soft-label">문제보다 존재 · 답보다 질문</span>
        </div>

        <div className="wallet-focus-v2-grid">
          <article className="wallet-focus-v2 primary">
            <div className="wallet-focus-v2-head">
              <span>현재 목표</span>
              {primaryGoal && <em>{goalTypeLabel(primaryGoal.goalType)}</em>}
            </div>
            {primaryGoal ? (
              <>
                <h3>{primaryGoal.title}</h3>
                {primaryGoal.description && <p>{primaryGoal.description}</p>}
                <div className="wallet-focus-v2-meta">
                  <span>{goalStatusLabel(primaryGoal.status)}</span>
                  <small>목표일 {formatDate(primaryGoal.targetOn)}</small>
                </div>
              </>
            ) : (
              <>
                <h3>아직 현재 목표가 정해지지 않았습니다.</h3>
                <p>진로교사와 함께 지금 가장 중요한 탐색 질문부터 만들어 보세요.</p>
              </>
            )}
          </article>

          <article className="wallet-focus-v2 next">
            <div className="wallet-focus-v2-head">
              <span>다음 행동</span>
              <em>NEXT</em>
            </div>
            {primaryAction ? (
              <>
                <h3>{primaryAction.title}</h3>
                {primaryAction.body && <p>{primaryAction.body}</p>}
                <div className="wallet-focus-v2-meta">
                  <span>한 걸음</span>
                  <small>{formatDate(primaryAction.actionOn || primaryAction.occurredAt)}</small>
                </div>
              </>
            ) : (
              <>
                <h3>다음 행동을 준비 중입니다.</h3>
                <p>현재 목표와 연결되는 작은 행동이 등록되면 이곳에서 바로 확인할 수 있습니다.</p>
              </>
            )}
          </article>
        </div>
      </section>

      <section className="wallet-home-section">
        <div className="wallet-home-heading">
          <div>
            <p className="eyebrow">MY GROWTH</p>
            <h2>쌓여가는 성장</h2>
          </div>
          <a href="/growth" className="wallet-inline-link">성장 상세 보기 →</a>
        </div>

        <div className="wallet-metric-v2-grid">
          <WalletMetric label="경험" value={overview.counts.experiences} note="참여·활동" />
          <WalletMetric label="평가" value={overview.counts.evaluations} note="확정 결과" />
          <WalletMetric label="성취" value={overview.counts.achievements} note="확인된 성장" />
          <WalletMetric label="Life Stamp" value={overview.counts.credentials} note="디지털배지" accent />
        </div>
      </section>

      {currentGoals.length>1 || latestActions.length>1 ? (
        <section className="wallet-home-section wallet-secondary-focus">
          <div className="wallet-home-heading">
            <div>
              <p className="eyebrow">FOCUS LIST</p>
              <h2>이어갈 것들</h2>
            </div>
          </div>
          <div className="wallet-secondary-grid">
            <div className="wallet-secondary-card">
              <strong>다른 목표</strong>
              {currentGoals.slice(1,4).map((goal)=>(
                <article key={goal.goalId}>
                  <span>{goalTypeLabel(goal.goalType)}</span>
                  <b>{goal.title}</b>
                  <small>{goalStatusLabel(goal.status)}</small>
                </article>
              ))}
              {currentGoals.length<=1 && <p className="case-empty">추가 목표가 없습니다.</p>}
            </div>
            <div className="wallet-secondary-card">
              <strong>다음 행동 목록</strong>
              {latestActions.slice(1,4).map((action)=>(
                <article key={action.entryId}>
                  <span>→</span>
                  <b>{action.title}</b>
                  <small>{formatDate(action.actionOn || action.occurredAt)}</small>
                </article>
              ))}
              {latestActions.length<=1 && <p className="case-empty">추가 행동이 없습니다.</p>}
            </div>
          </div>
        </section>
      ) : null}

      <section className="wallet-home-section wallet-timeline-v2">
        <div className="wallet-home-heading timeline-heading">
          <div>
            <p className="eyebrow">TIMELINE</p>
            <h2>나의 성장 시간축</h2>
            <p>학교생활에서 쌓인 경험, 검사, 성취, Life Stamp가 하나의 흐름으로 이어집니다.</p>
          </div>
          <span className="wallet-soft-label">{journey.timeline.length} records</span>
        </div>

        <div className="wallet-filter-v2">
          {[
            ["ALL","전체"],
            ["DIRECTION","방향·목표"],
            ["EXPERIENCE","경험·검사"],
            ["GROWTH","평가·성취"]
          ].map(([value,label])=>(
            <button
              key={value}
              className={filter===value ? "active" : ""}
              onClick={()=>setFilter(value)}
            >
              {label}
            </button>
          ))}
        </div>

        <div className="wallet-longitudinal-timeline v2">
          {filteredTimeline.map((item,index)=>{
            const experienceLink = item.eventType === "EXPERIENCE"
              && /^[0-9a-f-]{36}$/i.test(item.resourceId)
              ? `/experiences/school/${item.resourceId}`
              : null;
            return (
            <article
              className={"wallet-timeline-row event-"+item.eventType.toLowerCase()+(experienceLink ? " is-link" : "")}
              key={item.eventType+"-"+item.resourceId+"-"+index}
              role={experienceLink ? "link" : undefined}
              tabIndex={experienceLink ? 0 : undefined}
              onClick={experienceLink ? () => onNavigate(experienceLink) : undefined}
              onKeyDown={experienceLink ? (event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  onNavigate(experienceLink);
                }
              } : undefined}
              aria-label={experienceLink ? `${item.title} 경험 열기` : undefined}
            >
              <div className="wallet-timeline-date">{formatDate(item.occurredAt)}</div>
              <div className="wallet-timeline-marker"><span /></div>
              <div className="wallet-timeline-card">
                <span className="journey-type-chip">{eventLabel(item.eventType)}</span>
                <strong>{item.title}</strong>
                {item.description && <p>{item.description}</p>}
                {item.eventType==="COMPETENCY" && item.metadata?.score!=null && (
                  <small>역량점수 {String(item.metadata.score)}</small>
                )}
                {item.eventType==="EVALUATION" && item.metadata?.normalizedScore!=null && (
                  <small>정규화점수 {String(item.metadata.normalizedScore)}</small>
                )}
                {item.eventType==="CREDENTIAL" && item.metadata?.credentialStatus!=null && (
                  <small>Life Stamp 상태 {String(item.metadata.credentialStatus)}</small>
                )}
              </div>
            </article>
          );})}
          {!filteredTimeline.length && <p className="case-empty">표시할 성장 기록이 없습니다.</p>}
        </div>
      </section>
    </section>
  );
}

function QuickLink({
  href,icon,title,description
}:{
  href:string;icon:string;title:string;description:string;
}){
  return (
    <a className="wallet-quick-card" href={href}>
      <span className="wallet-quick-icon">{icon}</span>
      <span className="wallet-quick-copy">
        <strong>{title}</strong>
        <small>{description}</small>
      </span>
      <span className="wallet-quick-arrow">›</span>
    </a>
  );
}

function WalletMetric({
  label,value,note,accent=false
}:{
  label:string;value:number;note:string;accent?:boolean;
}){
  return (
    <article className={accent ? "wallet-metric-v2 accent" : "wallet-metric-v2"}>
      <span>{label}</span>
      <strong>{value}</strong>
      <small>{note}</small>
    </article>
  );
}
